import React, { useContext, useEffect } from 'react';
import { useLocation, Navigate } from 'react-router-dom';
import queryString from 'query-string';

import CurrentUserContext from 'contexts/CurrentUserContext';
import FeatureFlagContext from 'contexts/FeatureFlagContext';
import PretaxContext from 'contexts/PretaxContext';

import useUtilFilters from 'hooks/useUtilFilters';
import useQueryFormaCards from 'hooks/useQueryFormaCards';
import useQueryPretaxDependents from 'hooks/useQueryPretaxV2Dependents';
import useQueryProfile from 'hooks/useQueryProfile';

import Head from 'components/common/Head';
import PageSkeleton from 'components/common/PageSkeleton';

import useSnackbar from '@joinforma/ui/hooks/useSnackbar';

import FormaCardList from './FormaCardList';

import { FEATURE_FLAGS } from 'constants/featureFlag';
import { RootRoutes } from 'utils/routeGenerator';
import { isHSAAccount } from 'utils/pretaxAccount';
import { displayFormaCards } from 'utils/formaCards';
import { FormaCardMember } from './types';

const FormaCardsListPage = () => {
  const { employee } = useContext(CurrentUserContext);
  const feature_flag = useContext(FeatureFlagContext);

  const { pretax_accounts, updateWallets } = useContext(PretaxContext);

  const { showErrorSnackbar } = useSnackbar();
  const location = useLocation();

  const {
    full_name,
    first_name,
    last_name,
    employee_id,
    date_of_birth,
    email,
    settings: { address, primary_residence },
    company_detail: {
      features: { credit_card },
    },
  } = employee;

  const is_unit_forma_card_enabled = feature_flag.flag(
    FEATURE_FLAGS.UNIT_FORMA_CARD
  );

  const is_alegeus_migrated_customer = feature_flag.flag(
    FEATURE_FLAGS.ALEGEUS_MIGRATED_CUSTOMER
  );

  const { id }: { id?: string } = queryString.parse(location.search);

  const {
    filters,
    applyFilters,
  }: {
    filters: {
      id?: string;
    };
    applyFilters: (filters: { id?: string }) => void;
  } = useUtilFilters({ id });

  const { data: dependents_data, is_loading: is_dependents_data_loading } =
    useQueryPretaxDependents({
      is_pretax_enabled: true,
      onError: () =>
        showErrorSnackbar({ text: 'Failed loading eligible dependents' }),
    });

  const { data: profile_data, is_loading: is_profile_v3_loading } =
    useQueryProfile({
      isEnabled: Boolean(employee.id && is_alegeus_migrated_customer),
      use_v3_profile: is_alegeus_migrated_customer,
    });

  useEffect(() => {
    if (
      is_alegeus_migrated_customer &&
      !is_profile_v3_loading &&
      profile_data
    ) {
      const {
        employee: { employee_wallets: employee_wallets_v3 },
      } = profile_data;
      updateWallets(employee_wallets_v3);
    }
  }, [
    profile_data,
    is_alegeus_migrated_customer,
    is_profile_v3_loading,
    updateWallets,
  ]);

  const non_hsa_pretax_accounts = pretax_accounts.filter(
    ({ wallet_type_id }) => !isHSAAccount({ wallet_type: wallet_type_id })
  );

  const show_forma_card = displayFormaCards({
    is_unit_forma_card_enabled,
    has_non_hsa_pretax_accounts: non_hsa_pretax_accounts.length > 0,
    is_credit_card_enabled: credit_card,
  });

  const {
    data: forma_card_data,
    is_loading: is_forma_card_data_loading,
    refetch: refetchCards,
  } = useQueryFormaCards();

  if (!show_forma_card) {
    return <Navigate to={RootRoutes.home()} />;
  }

  if (is_forma_card_data_loading || is_dependents_data_loading) {
    return <PageSkeleton />;
  }

  const { forma_cards } = forma_card_data!;
  const { pretax_dependents: dependents } = dependents_data!;
  const filtered_user_id = id || employee_id;

  const cards = forma_cards.filter(
    ({ cardHolderReferenceId }) => cardHolderReferenceId === filtered_user_id
  );

  const forma_card_member: FormaCardMember = dependents.find(
    (dependent) => dependent?.id === filters?.id
  ) || {
    first_name,
    last_name,
    date_of_birth,
    email,
    employee_id,
    id: employee_id,
    billing_address: primary_residence,
    mailing_address: address,
  };

  return (
    <>
      <Head title={'Forma Card'} />
      <FormaCardList
        forma_card_member={forma_card_member}
        employee_full_name={full_name}
        employee_id={employee_id}
        dependents={dependents}
        cards={cards}
        filtered_id={filters.id || employee_id}
        applyFilters={applyFilters}
        employee_primary_residence={primary_residence}
        refetchCards={refetchCards}
      />
    </>
  );
};

export default FormaCardsListPage;
